import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { bffService } from '../../../../../../services/utils/axios'
import { formatDate } from '../../../components/formatDate'

/**
 * ConsumerConfirmUserDetails Component
 *
 * This component displays a confirmation view of all consumer-specific contract details
 * before final submission. It shows information including:
 * - Contract information (number, type, status)
 * - Personal information (name, address, contact details)
 * - Billing information
 * - Banking details
 * - Meter/consumption details
 * - Energy preferences and delivery information
 * - Terms acceptance and marketing preferences
 *
 * The component also handles the API call to update the contract when confirmed.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.payload - The contract data to display for confirmation
 * @param {Function} props.openFeedback - Function to display success feedback after successful update
 * @param {number} props.submit - Trigger for API submission (incremented to trigger)
 * @param {Function} props.setError - Function to display error feedback if update fails
 * @param {Function} props.setSubmit - Function to reset the submit counter
 * @param {Function} props.refetch - Function to refresh contract data after successful update
 *
 * @example
 * <ConsumerConfirmUserDetails
 *   payload={contractData}
 *   openFeedback={() => setFeedback(true)}
 *   submit={submitCounter}
 *   setError={() => setErrorState(true)}
 *   setSubmit={setSubmitCounter}
 *   refetch={refreshData}
 * />
 *
 * @returns {React.ReactElement} A confirmation view of consumer contract details
 */
function ConsumerConfirmUserDetails({
  payload,
  openFeedback,
  submit,
  setError,
  setSubmit,
  refetch,
}) {
  const { t } = useTranslation()

  useEffect(() => {
    if (submit > 0) updateContractDetails()
    setSubmit(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submit])

  /**
   * This api call is used to update the single contract data using PATCH /contracts/{uuid}.
   * It returns the updated contract data if successful.
   */
  const updateContractDetails = async () => {
    try {
      const response = await bffService.patch(
        `/contracts/${payload?.uuid}`,
        payload
      )

      if (response.data) {
        refetch()
        openFeedback()
      } else {
        setError(true)
      }
    } catch (error) {
      console.error('Error updating contract:', error)
      setError(true)
    }
  }

  let plantAddressDescription = ''
  if (payload?.isPlantAddressSameAsAddress) {
    plantAddressDescription =
      payload?.address?.postalCode + ' ' + payload?.address?.city
  } else {
    plantAddressDescription =
      payload?.plantAddress?.postalCode + ' ' + payload?.plantAddress?.city
  }

  let billingAddressName = ''
  let billingAddressStreet = ''
  let billingAddressHouseNumber = ''
  let billingAddressPostalCode = ''
  let billingAddressCity = ''
  if (payload?.isBillingAddressSameAsAddress) {
    billingAddressName = payload?.firstName + ' ' + payload?.lastName
    billingAddressStreet = payload?.address?.street
    billingAddressHouseNumber = payload?.address?.houseNumber
    billingAddressPostalCode = payload?.address?.postalCode
    billingAddressCity = payload?.address?.city
  } else {
    billingAddressName = payload?.billingAddress?.name
    billingAddressStreet = payload?.billingAddress?.street
    billingAddressHouseNumber = payload?.billingAddress?.houseNumber
    billingAddressPostalCode = payload?.billingAddress?.postalCode
    billingAddressCity = payload?.billingAddress?.city
  }

  return (
    <>
      {/* Personal Information */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLContractsNumber')}</Typography>
        <Typography variant="body1" align="right" id="contractNumber">
          {payload?.contractNumber}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLContractType')}</Typography>
        <Typography variant="body1" align="right" id="contractType">
          {t('LBLConsumer')}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLContaractStatus')}</Typography>
        <Typography variant="body1" align="right" id="contractStatus">
          {payload?.contractStatus === 'ACTIVE'
            ? t('LBLActive')
            : payload?.contractStatus === 'INACTIVE'
            ? t('LBLInactive')
            : t('LBLInProgress')}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">Name</Typography>
        <Typography variant="body1" align="right" id="fullName">
          {t(payload?.salutation) +
            ' ' +
            (payload?.firstName && payload?.lastName
              ? `${payload.firstName} ${payload.lastName}`
              : '')}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLAddress')}</Typography>
        <Stack my={2}>
          <Typography variant="body1" align="right" id="address1">
            {payload?.address?.street +
              ' ' +
              payload?.address?.houseNumber +
              ','}
          </Typography>
          <Typography variant="body1" align="right" id="address2">
            {payload?.address?.postalCode + ' ' + payload?.address?.city}
          </Typography>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">E-Mail</Typography>
        <Typography variant="body1" align="right" id="email">
          {payload?.email}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLHandy')}</Typography>
        <Typography variant="body1" align="right" id="phoneNumber">
          {payload?.phone}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLBirthDate')}</Typography>
        <Typography variant="body1" align="right" id="dateOfBirth">
          {formatDate(payload?.dateOfBirth)}
        </Typography>
      </Stack>

      {/* Billing Information */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLBillingAddressOwner')}</Typography>
        <Typography variant="body1" align="right" id="billingOwner">
          {billingAddressName}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLBillingAddress')}</Typography>
        <Stack my={2}>
          <Typography variant="body1" align="right" id="billingAddress1">
            {billingAddressStreet + ' ' + billingAddressHouseNumber + ','}
          </Typography>
          <Typography variant="body1" align="right" id="billingAddress2">
            {billingAddressPostalCode + ' ' + billingAddressCity}
          </Typography>
        </Stack>
      </Stack>

      {/* Banking Information */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLBankName')}</Typography>
        <Typography variant="body1" align="right" id="creditInstituteName">
          {payload?.paymentDetails?.creditInstituteName}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLBic')}</Typography>
        <Typography variant="body1" align="right" id="bic">
          {payload?.paymentDetails?.bic}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLIban')}</Typography>
        <Typography variant="body1" align="right" id="iban">
          {payload?.paymentDetails?.iban}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLAccountHolder')}</Typography>
        <Typography variant="body1" align="right" id="accountHolderName">
          {payload?.paymentDetails?.accountHolderName}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLAcceptCondition')}</Typography>
        <Typography variant="body1" align="right" id="bankAccept">
          {payload?.paymentDetails?.sepaAccepted
            ? t('UserDetailSelected')
            : t('UserDetailNotSelected')}
        </Typography>
      </Stack>

      {/* Preference and Delivery */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLPreference')}</Typography>
        <Typography variant="body1" align="right" id="preference">
          {t(payload?.energyPreference)}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLStartOfDeliveryDate')}</Typography>
        <Typography variant="body1" align="right" id="startOfDelivery">
          {payload?.isStartOfDeliveryNextPossibleDate
            ? t('NextPossibleDate')
            : formatDate(payload?.startOfDeliveryDate)}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLPlantAdress')}</Typography>
        <Stack my={2}>
          <Typography variant="body1" align="right" id="plantAddress">
            {plantAddressDescription}
          </Typography>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLMarketLocation')}</Typography>
        <Typography variant="body1" align="right" id="marketLocation">
          {payload?.marketLocation}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLMeterNumber')}</Typography>
        <Typography variant="body1" align="right" id="meterNumber">
          {payload?.meterNumber}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLLastCustomerNum')}</Typography>
        <Typography variant="body1" align="right" id="lastCustomerNumber">
          {payload?.utilityContractNumber}
        </Typography>
      </Stack>

      {/* Terms and Marketing */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLAgb')}</Typography>
        <Typography variant="body1" align="right" id="agb">
          {payload?.isTermsAndConditionsAccepted
            ? t('UserDetailSelected')
            : t('UserDetailNotSelected')}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="body1">{t('LBLMarketing')}</Typography>
        <Typography variant="body1" align="right" id="marketing">
          {payload?.isMarketingInformationConsentGiven
            ? t('UserDetailSelected')
            : t('UserDetailNotSelected')}
        </Typography>
      </Stack>
    </>
  )
}

ConsumerConfirmUserDetails.propTypes = {
  payload: PropTypes.objectOf(PropTypes.any).isRequired,
  openFeedback: PropTypes.func.isRequired,
  submit: PropTypes.number.isRequired,
  setError: PropTypes.func.isRequired,
  setSubmit: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
}

export default ConsumerConfirmUserDetails
